import React, { forwardRef, useRef, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import {
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListProps,
  TextField,
  useTheme,
} from '@mui/material';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import { TooltipIconButton } from '@paragon/client-tools';
import { ProjectSummary } from '@paragon/contract-library';
import { createScrollbarStyling } from '@paragon/themes';

const MUIVirtuosoConfig = {
  List: forwardRef<HTMLDivElement, ListProps>(({ style, children }, listRef) => {
    return (
      <List style={{ padding: 0, ...style, margin: 0 }} component="div" ref={listRef}>
        {children}
      </List>
    );
  }),
};

interface Props {
  designUrl: string;
  projectSummaries: readonly ProjectSummary[];
}

/**
 * For use by the `ChooseProjectDialog` only.
 */
export function PickProjectPanel({ designUrl, projectSummaries }: Props) {
  const theme = useTheme();

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const visibleRangeRef = useRef({ startIndex: 0, endIndex: 0 });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProjectIndex, setSelectedProjectIndex] = useState(0);

  const filteredProjectSummaries = projectSummaries.filter((projectSummary) =>
    projectSummary.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  function handleSearchTermChange(term: string) {
    setSearchTerm(term);
    setSelectedProjectIndex(0);
    virtuosoRef.current?.scrollToIndex({ index: 0, align: 'start' });
  }

  function handleSelectedProjectIndexChange(index: number) {
    if (index < 0) return;

    setSelectedProjectIndex(index);

    const visibleRange = visibleRangeRef.current;
    if (index < visibleRange.startIndex + 1 || index > visibleRange.endIndex - 1) {
      virtuosoRef.current?.scrollToIndex({ index, align: 'center' });
    }
  }

  function handlePickProject(summary: ProjectSummary, options?: { openInNewTab?: boolean }) {
    const target = `${designUrl}${summary.guid}`;

    if (options?.openInNewTab ?? false) {
      window.open(target, '_blank');
    } else {
      window.location.href = target;
    }
  }

  function handleKeyboardSelectionTriggered() {
    const selectedSummary = filteredProjectSummaries[selectedProjectIndex];
    if (selectedSummary != null) {
      handlePickProject(selectedSummary);
    }
  }

  return (
    <>
      <TextField
        fullWidth
        autoFocus
        autoComplete="off"
        sx={{ marginTop: 1 }}
        label="Search"
        placeholder="Start typing to search for a project"
        value={searchTerm}
        onChange={(event) => handleSearchTermChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.code === 'Escape' && searchTerm !== '') {
            event.stopPropagation();
            handleSearchTermChange('');
          } else if (event.code === 'Enter') {
            event.stopPropagation();
            handleKeyboardSelectionTriggered();
          } else if (event.code === 'ArrowUp') {
            event.stopPropagation();
            handleSelectedProjectIndexChange(Math.max(selectedProjectIndex - 1, 0));
          } else if (event.code === 'ArrowDown') {
            event.stopPropagation();
            handleSelectedProjectIndexChange(Math.min(selectedProjectIndex + 1, filteredProjectSummaries.length - 1));
          }
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),

            endAdornment: searchTerm !== '' && (
              <InputAdornment position="end">
                <TooltipIconButton enableInteractive title="Clear Search" onClick={() => handleSearchTermChange('')}>
                  <ClearIcon />
                </TooltipIconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      <Virtuoso
        ref={virtuosoRef}
        components={MUIVirtuosoConfig}
        data={filteredProjectSummaries}
        rangeChanged={(range) => {
          visibleRangeRef.current = range;
        }}
        style={{
          minHeight: '400px',
          ...createScrollbarStyling(theme),
        }}
        computeItemKey={(_, projectSummary) => projectSummary.guid}
        itemContent={(index, projectSummary) => (
          <ListItem
            disablePadding
            secondaryAction={
              <TooltipIconButton
                enableInteractive
                title="Open in new tab"
                onClick={() => handlePickProject(projectSummary, { openInNewTab: true })}
              >
                <OpenInNewIcon />
              </TooltipIconButton>
            }
          >
            <ListItemButton selected={index === selectedProjectIndex} onClick={() => handlePickProject(projectSummary)}>
              <ListItemText
                primary={projectSummary.name}
                secondary={`Created: ${Intl.DateTimeFormat('en-US').format(new Date(projectSummary.createdAt))}`}
              />
            </ListItemButton>
          </ListItem>
        )}
      />
    </>
  );
}
